import { ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertProps,
  AlertTitle,
  Box,
} from "@chakra-ui/react";
import { StripePaymentStatus } from "__generated__/serverGql/graphql";
import PageLoadingSpinner from "components/Shared/PageLoadingSpinner";
import ContactEmailLink from "components/Shared/ContactEmailLink";
import { AssertionError } from "utils/assertions";

type Props = {
  alertProps?: Omit<AlertProps, "children" | "status">;
  error?: Error;
  isLoading: boolean;
  isLoadingAlertShown?: boolean;
  paymentStatus?: StripePaymentStatus | null;
};

export default function CheckoutSessionResultAlert({
  alertProps,
  error,
  isLoading,
  isLoadingAlertShown = true,
  paymentStatus,
}: Props) {
  if (isLoading && !isLoadingAlertShown) {
    return <PageLoadingSpinner minHeight="12rem" />;
  }

  const alertConfig = ((): Pick<AlertProps, "status"> & {
    title: ReactNode;
    description: ReactNode;
  } => {
    if (isLoading) {
      return {
        status: "loading",
        title: "Processing your subscription…",
        description: "Please wait while we set up your billing.",
      };
    }

    if (error || !paymentStatus) {
      return {
        status: "error",
        title: "Unable to process subscription",
        description: error?.message || "Failed to process checkout session",
      };
    }

    if (
      paymentStatus === StripePaymentStatus.PAID ||
      paymentStatus === StripePaymentStatus.NO_PAYMENT_REQUIRED
    ) {
      return {
        status: "success",
        title: "Subscription activated!",
        description:
          "Your subscription has been successfully activated. Thank you!",
      };
    }

    if (paymentStatus === StripePaymentStatus.UNPAID) {
      return {
        status: "warning",
        title: "Payment incomplete",
        description: (
          <>
            Your payment is still being processed. Some payment methods, like
            ACH, can take time to process. If your payment method requires
            additional steps, please check your email for instructions from
            Stripe. If you continue to have issues, please contact{" "}
            <ContactEmailLink fontWeight="semibold" /> for assistance.
          </>
        ),
      };
    }

    throw new AssertionError(
      `Unexpected payment status: ${paymentStatus as string}`,
    );
  })();

  return (
    <Alert status={alertConfig.status} {...alertProps}>
      <AlertIcon />
      <Box>
        <AlertTitle>{alertConfig.title}</AlertTitle>
        <AlertDescription>{alertConfig.description}</AlertDescription>
      </Box>
    </Alert>
  );
}
